import { useContext, useCallback, useEffect, useState } from 'react';
import { BootstrapContext } from 'app/bootstrap/BootstrapContext';
import history from '@history';
import { useDispatch, useSelector } from 'react-redux';
import { yupResolver } from '@hookform/resolvers/yup';
import { Controller, useForm } from 'react-hook-form';
import { styled } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import WdPageSimple from '@main/core/WdPageSimple';
import { ButtonWithLoader, ComposedHeader, FormCardLayout } from '@main/components';
import _ from 'lodash';
import { useRequest } from '@main/hooks';
import {
  getBillingInfo,
  updateBillingInfo,
  catalogTaxRegimes,
  catalogCFDI,
  catalogCities,
  catalogStates,
  catalogCountries,
} from 'src/app/api/company/billing';
import { Divider, Grid, Typography } from '@mui/material';
import { useParams } from 'react-router-dom';
import { AutocompleteSelector, PhoneCodeSelect, DirectionFields } from 'src/app/components';
import { showMessage } from 'app/store/core/messageSlice';
import { buildAddressFormState } from 'src/app/utils/address';
import { defaultValues } from '../Defaults/tax-information';
import { schema } from '../Validators/tax-information';

const Root = styled(WdPageSimple)(({ theme }) => ({
  backgroundColor: '#FFF',
  '& .WdPageSimple-header': {},
  '& .WdPageSimple-content': {},
}));

const transformToObject = (data, valueKey, labelKey) => {
  return {
    value: data[valueKey],
    label: data[labelKey],
  };
};

const initialAddress = {
  addressStreet: '',
  addressColony: '',
  addressState: null,
  addressCity: null,
  addressZip: '',
  addressExtNumber: '',
  addressIntNumber: '',
  addressCountry: null,
};

const TaxInformation = () => {
  // --- Redux & Config ---
  const dispatch = useDispatch();
  const { finalizeSession } = useContext(BootstrapContext);
  const { config } = useSelector((state) => state.config);
  const { id: previousId } = useParams();

  // --- Address state ---
  const [address, setAddress] = useState(initialAddress);
  const [addressError, setAddressError] = useState(false);

  // --- Form setup ---
  const { control, formState, handleSubmit, setValue } = useForm({
    mode: 'onChange',
    defaultValues,
    resolver: yupResolver(schema),
  });
  const { isValid, dirtyFields, errors } = formState;

  // --- Request handlers ---
  const handleOnForbidden = async () => {
    await finalizeSession();
  };
  const handleOnError = (message) => dispatch(showMessage({ message }));

  const { makeRequest: getPreviousData, response: responsePreviousData } = useRequest(
    getBillingInfo,
    {
      onForbidden: handleOnForbidden,
      onError: handleOnError,
    }
  );

  const { makeRequest: tryUpdate, isLoading } = useRequest(updateBillingInfo, {
    onError: handleOnError,
  });

  // --- Helpers ---
  const OMIT_KEYS = [
    'updated_at',
    'created_at',
    'id',
    'image',
    'metadata',
    'addressCountry',
    'addressState',
    'addressCity',
    'paymentMethod',
    'addressCountryName',
    'addressStateName',
    'addressCityName',
    'fiscalRegimeName',
    'cfdiUseName',
    'paymentMethodName',
  ];
  const NEED_TRANSFORM = ['fiscalRegime', 'cfdiUse'];

  const handleLoadToForm = (previousData) => {
    const result = _.omit(previousData, OMIT_KEYS);

    setAddress(buildAddressFormState(previousData));

    Object.entries(result).forEach(([key, value]) => {
      let newValue = value;

      if (key === 'responsiblePhoneCode') {
        newValue = value ? (value.startsWith('+') ? value : `+${value}`) : '';
      } else if (NEED_TRANSFORM.includes(key)) {
        newValue = transformToObject(previousData, key, `${key}Name`);
      }

      setValue(key, newValue, { shouldValidate: true, shouldDirty: true });
    });
  };

  // --- Effects ---
  useEffect(() => {
    if (previousId) getPreviousData(previousId);
  }, [previousId]);

  useEffect(() => {
    if (responsePreviousData) handleLoadToForm(responsePreviousData);
  }, [responsePreviousData]);

  // --- Update handler ---
  const handleUpdate = (form) => {
    const builtForm = {
      ...form,
      ...address,
      addressCountry: address.addressCountry?.value,
      addressCity: address.addressCity?.value,
      addressState: address.addressState?.value,
      cfdiUse: form.cfdiUse.value,
      fiscalRegime: form.fiscalRegime.value,
    };

    tryUpdate(previousId, builtForm).then(({ isOk }) => {
      if (isOk) {
        dispatch(
          showMessage({
            message: 'Datos fiscales actualizados correctamente',
            autoHideDuration: 4000,
            anchorOrigin: { vertical: 'top', horizontal: 'center' },
            variant: 'success',
          })
        );
        history.replace('/companies');
      }
    });
  };

  // --- Address change handler ---
  const handleDirectionOnChange = useCallback(
    (field, value) => setAddress((curr) => ({ ...curr, [field]: value })),
    []
  );

  return (
    <Root
      header={<ComposedHeader decorator="button" />}
      content={
        <Box
          className="flex justify-center items-start"
          sx={{
            width: '100%',
            height: '100%',
          }}
        >
          <FormCardLayout title="Datos Fiscales">
            <FormCardLayout.FullColum>
              <form
                name="taxInformationForm"
                noValidate
                className="flex flex-col justify-center w-full"
                autoComplete="off"
              >
                <div className="flex flex-col">
                  <Divider className="mb-24">
                    <Typography className="text-16 mb-8">Datos de contacto</Typography>
                  </Divider>
                  <Controller
                    name="responsibleName"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        className="mb-32"
                        label="Nombre"
                        autoFocus
                        type="text"
                        error={!!errors.responsibleName}
                        helperText={errors?.responsibleName?.message}
                        variant="outlined"
                        required
                        fullWidth
                      />
                    )}
                  />
                  <Controller
                    name="responsibleEmail"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        className="mb-32"
                        label="Correo electrónico"
                        autoFocus
                        type="email"
                        error={!!errors.responsibleEmail}
                        helperText={errors?.responsibleEmail?.message}
                        variant="outlined"
                        fullWidth
                        onChange={(current) => {
                          field.onChange(current);
                        }}
                      />
                    )}
                  />
                  <Grid className="mb-32" spacing={2.5} container>
                    <Grid xs={3} item>
                      <Controller
                        name="responsiblePhoneCode"
                        control={control}
                        render={({ field }) => (
                          <PhoneCodeSelect
                            {...field}
                            type="text"
                            error={!!errors.responsiblePhoneCode}
                            helperText={errors?.responsiblePhoneCode?.message}
                            variant="outlined"
                            required
                            fullWidth
                            inputProps={{
                              style: {
                                height: '5.3rem',
                              },
                            }}
                          />
                        )}
                      />
                    </Grid>
                    <Grid xs={9} item>
                      <Controller
                        name="responsiblePhone"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            label="Número telefónico"
                            autoFocus
                            type="number"
                            error={!!errors.responsiblePhone}
                            helperText={errors?.responsiblePhone?.message}
                            variant="outlined"
                            fullWidth
                            InputProps={{
                              inputProps: {
                                style: {
                                  WebkitAppearance: 'none',
                                  margin: 0,
                                  MozAppearance: 'textfield',
                                },
                              },
                            }}
                          />
                        )}
                      />
                    </Grid>
                  </Grid>
                  <Divider className="mb-24 mt-32">
                    <Typography className="text-16 mb-8">Datos fiscales</Typography>
                  </Divider>
                  <Controller
                    name="fiscalRegime"
                    control={control}
                    render={({ field }) => (
                      <AutocompleteSelector
                        {...field}
                        className="w-full mb-32"
                        request={catalogTaxRegimes}
                        id="scopes-data-suspen"
                        valueKey="value"
                        labelKey="label"
                        ChipProps={{ color: 'secondary' }}
                        inputProps={{
                          fullWidth: true,
                          variant: 'outlined',
                          label: 'Régimen fiscal',
                          placeholder: 'Seleccionar una opción',
                          error: !!errors.fiscalRegime,
                          helperText: errors?.fiscalRegime?.message,
                        }}
                      />
                    )}
                  />
                  <Controller
                    name="rfc"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        className="mb-32"
                        label="RFC"
                        autoFocus
                        type="string"
                        error={!!errors?.rfc}
                        helperText={errors?.rfc?.message}
                        variant="outlined"
                        fullWidth
                        required
                      />
                    )}
                  />
                  <Controller
                    name="businessName"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        className="mb-32"
                        label="Razón Social"
                        autoFocus
                        type="string"
                        error={!!errors?.businessName}
                        helperText={errors?.businessName?.message}
                        variant="outlined"
                        fullWidth
                        required
                      />
                    )}
                  />
                  <Controller
                    name="cfdiUse"
                    control={control}
                    render={({ field }) => (
                      <AutocompleteSelector
                        {...field}
                        className="w-full mb-32"
                        request={catalogCFDI}
                        id="scopes-data-suspen"
                        valueKey="value"
                        labelKey="label"
                        ChipProps={{ color: 'secondary' }}
                        inputProps={{
                          fullWidth: true,
                          variant: 'outlined',
                          label: 'Uso CFDI',
                          placeholder: 'Seleccionar una opción',
                          error: !!errors.cfdiUse,
                          helperText: errors?.cfdiUse?.message,
                        }}
                      />
                    )}
                  />
                  <Divider className="mb-24 mt-32">
                    <Typography className="text-16 mb-8">Domicilio fiscal</Typography>
                  </Divider>
                  <DirectionFields
                    catalogCountries={catalogCountries}
                    catalogStates={catalogStates}
                    catalogCities={catalogCities}
                    control={control}
                    errors={errors}
                    onChange={handleDirectionOnChange}
                    spacing={3.5}
                    required
                    onError={setAddressError}
                    values={address}
                  />
                </div>
              </form>
            </FormCardLayout.FullColum>
            <FormCardLayout.RowButton>
              <Button
                onClick={() => history.back()}
                variant="contained"
                className="w-full md:w-auto"
                color="error"
              >
                Cancelar
              </Button>
              <ButtonWithLoader
                onClick={handleSubmit(handleUpdate)}
                color="success"
                aria-label="Actualizar compañía"
                variant="contained"
                disabled={!isValid || addressError}
                type="submit"
                isLoading={isLoading}
              >
                Guardar
              </ButtonWithLoader>
            </FormCardLayout.RowButton>
          </FormCardLayout>
        </Box>
      }
    />
  );
};

export default TaxInformation;
